import axios, { isAxiosError, type AxiosError } from "axios";
import i18n from "i18next";

import { type ErrorResponse } from "@/api-v3/model";
import { type NmibleAxiosRequestConfig } from "@/api-v3/mutator/axios-instance-v3";
import { toast } from "@/hooks/use-toast";

import { isApiValidationError } from "./api-validation-errors";

enum NETWORK_ERROR_CODES {
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  TIMEOUT = 408,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
  TOO_MANY_REQUESTS = 522,
  BAD_GATEWAY = 523,
  SERVICE_UNAVAILABLE_RETRY = 524,
  NETWORK_READ_TIMEOUT = 599,
}

/**
 * Backend error codes the app reacts to by code rather than by status.
 *
 * PASS answers PMT-05 with 422, the same status FastAPI uses for its own
 * `{ detail: [...] }` validation errors, so the two can only be told apart by
 * the body shape. Never branch on the status here.
 */
export const API_ERROR_CODES = {
  /** The Default Payment Account cannot receive this kind of Payable Item. */
  PAYMENT_ACCOUNT_CANNOT_RECEIVE: "PMT-05",
  /** What was asked for isn't there, or isn't the caller's. */
  NOT_FOUND: "COM-02",
} as const;

export interface ParsedApiError {
  title: string;
  code: string;
  details: string;
  traceId?: string;
}

export function isValidErrorResponse(error: unknown): error is ErrorResponse {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    "title" in error &&
    "details" in error &&
    typeof (error as ErrorResponse).code === "string" &&
    typeof (error as ErrorResponse).title === "string" &&
    typeof (error as ErrorResponse).details === "string"
  );
}

const defaultError = {
  title: "error",
  message: "error_somethingWentWrong",
};

export const parseError = (error: unknown) => {
  if (!error) {
    return defaultError;
  }

  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data;

    if (isValidErrorResponse(errorData)) {
      return {
        title: `${errorData.title} (${errorData.code})`,
        message: errorData.details,
      };
    }

    return {
      title: defaultError.title,
      message: error.message || defaultError.message,
    };
  }

  return defaultError;
};

const getTraceId = (error: AxiosError): string | undefined => {
  const header = error.response?.headers?.["x-trace-id"];
  return typeof header === "string" ? header : undefined;
};

export const parseApiError = (error: unknown): ParsedApiError | null => {
  if (!isAxiosError(error)) {
    return null;
  }
  const errorData = error.response?.data;
  if (!isValidErrorResponse(errorData)) {
    return null;
  }
  return {
    title: errorData.title,
    code: errorData.code,
    details: errorData.details,
    traceId: getTraceId(error),
  };
};

/**
 * PMT-05: the Default Payment Account cannot receive this Payable Item kind.
 * Recognised by the `ErrorResponse` body shape (`parseApiError` only answers
 * for a body with string `code`/`title`/`details`), so a FastAPI validation
 * body on the same 422 never matches.
 */
export const isPaymentAccountCannotReceiveError = (error: unknown): boolean =>
  parseApiError(error)?.code === API_ERROR_CODES.PAYMENT_ACCOUNT_CANNOT_RECEIVE;

/**
 * Whether the request that failed declared this backend error code as one it
 * renders in place. Scoped to the single request, so a claim flow's own PMT-05
 * is silent while the same code from anywhere else still toasts.
 */
const rendersErrorInline = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }
  const declared = (error.config as NmibleAxiosRequestConfig | undefined)
    ?.inlineErrorCodes;
  const code = parseApiError(error)?.code;
  return Boolean(declared && code && declared.includes(code));
};

const renderDescription = (
  errorData: ErrorResponse,
  traceId: string | undefined
) => (
  <span>
    {errorData.details}
    {traceId && <span className="block italic text-gray-500">{traceId}</span>}
  </span>
);

export interface QueryErrorMeta {
  /** Set on a query/mutation `meta` when the caller renders errors inline. */
  skipGlobalErrorToast?: boolean;
}

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: QueryErrorMeta;
    mutationMeta: QueryErrorMeta;
  }
}

export const handleQueryError = (error: unknown, meta?: QueryErrorMeta) => {
  if (meta?.skipGlobalErrorToast) {
    return;
  }

  const status = isAxiosError(error) ? error.response?.status : null;

  if (
    status === NETWORK_ERROR_CODES.UNAUTHORIZED ||
    status === NETWORK_ERROR_CODES.FORBIDDEN
  ) {
    return;
  }

  if (isApiValidationError(error)) {
    // Field-level UI is handled inline by the form.
    return;
  }

  if (rendersErrorInline(error)) {
    // The request said it answers this code itself, on the step that failed
    // (the claim flows do this for PMT-05). A toast on top would duplicate it.
    return;
  }

  if (
    isAxiosError(error) &&
    (error.config as NmibleAxiosRequestConfig | undefined)?.skipGlobalErrorToast
  ) {
    return;
  }

  if (isAxiosError(error)) {
    const errorData = error.response?.data;
    if (isValidErrorResponse(errorData)) {
      toast({
        title: `${errorData.title} (${errorData.code})`,
        description: renderDescription(errorData, getTraceId(error)),
        variant: "default",
      });
      return;
    }
  }

  toast({
    title: i18n.t("error"),
    description: i18n.t("unexpectedError"),
    variant: "default",
  });
};
